import React, { useState, useEffect } from 'react';
import { getBlogPostBySlug } from '../services/blogService';
import type { BlogPost } from '../lib/supabase';
import { Loader } from './Loader';
import { ErrorMessage } from './ErrorMessage';

interface BlogDetailPageProps {
  slug: string;
  onBack: () => void;
}

export const BlogDetailPage: React.FC<BlogDetailPageProps> = ({ slug, onBack }) => {
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadPost();
  }, [slug]);

  const loadPost = async () => {
    setLoading(true);
    setError('');
    try {
      const blogPost = await getBlogPostBySlug(slug);
      if (!blogPost) {
        setError('Blog post not found');
      } else {
        console.log('Blog post loaded:', {
          title: blogPost.title,
          has_cover_image: !!blogPost.cover_image,
          cover_image_preview: blogPost.cover_image?.substring(0, 50),
          images_count: blogPost.images?.length || 0,
          images_preview: blogPost.images?.map(img => ({
            length: img?.length,
            starts_with: img?.substring(0, 30)
          })),
          content_preview: blogPost.content?.substring(0, 200)
        });
        setPost(blogPost);
        // Update page title for SEO
        document.title = `${blogPost.title} - CompareLens`;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load blog post');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getReadTime = (content: string) => {
    const words = content.split(/\s+/).filter(w => w.length > 0).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min read`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-20">
        <div className="max-w-4xl mx-auto px-4">
          <Loader />
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-20">
        <div className="max-w-4xl mx-auto px-4">
          <ErrorMessage message={error || 'Blog post not found'} />
          <button
            onClick={onBack}
            className="mt-6 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            ← Back to Blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface-1)]">
      {/* Header with back button */}
      <div className="bg-[var(--surface-2)] border-b border-[var(--surface-4)] sticky top-0 z-10 backdrop-blur-lg bg-opacity-90">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-[var(--text-2)] hover:text-[var(--brand)] transition"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Blog
          </button>
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 py-12">
        {/* Cover Image */}
        {post.cover_image && (
          <div className="mb-8 rounded-2xl overflow-hidden shadow-lg">
            <img 
              src={post.cover_image} 
              alt={post.title}
              className="w-full h-64 md:h-96 object-cover"
              onError={(e) => {
                console.error('Failed to load cover image:', post.cover_image?.substring(0, 100));
                (e.target as HTMLImageElement).style.display = 'none';
              }}
              onLoad={() => {
                console.log('Cover image loaded successfully');
              }}
            />
          </div>
        )}

        {/* Header */}
        <header className="mb-8">
          {/* AI Generated Badge */}
          <div className="mb-4">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-[var(--brand-lighter)] text-[var(--brand)] rounded-full text-sm font-semibold">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 7H7v6h6V7z" />
                <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
              </svg>
              AI Generated
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-[var(--text-1)] mb-4 leading-tight font-serif">
            {post.title}
          </h1>

          {/* Excerpt */}
          {post.excerpt && (
            <p className="text-xl text-[var(--text-2)] mb-6 leading-relaxed">
              {post.excerpt}
            </p>
          )}

          {/* Meta information */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-[var(--text-3)] pb-6 border-b border-[var(--surface-4)]">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {formatDate(post.created_at)}
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {getReadTime(post.content)}
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              {post.view_count || 0} views
            </div>
          </div>

          {/* Laptops mentioned */}
          {post.laptops_mentioned && post.laptops_mentioned.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {post.laptops_mentioned.map((laptop, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-[var(--surface-2)] text-[var(--text-2)] rounded-lg border border-[var(--surface-4)] text-sm font-medium"
                >
                  {laptop}
                </span>
              ))}
            </div>
          )}
        </header>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <div className="bg-[var(--surface-2)] rounded-2xl shadow-sm p-8 md:p-12 border border-[var(--surface-4)]">
            {post.content.split('\n').map((paragraph, idx) => {
              if (!paragraph.trim()) return null;

              // Handle images ![Image] or ![Image 1] etc
              const imageMatch = paragraph.match(/^!\[Image\s*(\d*)\]/);
              if (imageMatch) {
                console.log('Found image placeholder:', paragraph);
                if (post.images && post.images.length > 0) {
                  const imageIndex = imageMatch[1] ? parseInt(imageMatch[1]) - 1 : 0;
                  const imageUrl = post.images[imageIndex];
                  const caption = post.image_captions?.[imageIndex];
                  
                  console.log(`Rendering image ${imageIndex + 1}:`, {
                    has_url: !!imageUrl,
                    url_length: imageUrl?.length,
                    url_starts: imageUrl?.substring(0, 30)
                  });
                  
                  if (imageUrl) {
                    return (
                      <figure key={idx} className="my-8">
                        <img 
                          src={imageUrl} 
                          alt={caption || `Image ${imageIndex + 1}`}
                          className="w-full rounded-xl shadow-lg"
                          onError={(e) => {
                            console.error(`Failed to load image ${imageIndex + 1}:`, imageUrl?.substring(0, 100));
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                          onLoad={() => {
                            console.log(`Image ${imageIndex + 1} loaded successfully`);
                          }}
                        />
                        {caption && (
                          <figcaption className="text-center text-sm text-[var(--text-3)] mt-3 italic">
                            {caption}
                          </figcaption>
                        )}
                      </figure>
                    );
                  } else {
                    console.warn(`No image URL found for index ${imageIndex}`);
                  }
                } else {
                  console.warn('Image placeholder found but post.images is empty or undefined');
                }
                return null;
              }
              
              // Handle headers
              if (paragraph.startsWith('###')) {
                return (
                  <h3 key={idx} className="text-2xl font-bold mt-8 mb-4 text-[var(--text-1)]">
                    {paragraph.replace(/^###\s*/, '')}
                  </h3>
                );
              }
              if (paragraph.startsWith('##')) {
                return (
                  <h2 key={idx} className="text-3xl font-bold mt-10 mb-6 text-[var(--text-1)]">
                    {paragraph.replace(/^##\s*/, '')}
                  </h2>
                );
              }
              if (paragraph.startsWith('#')) {
                return (
                  <h1 key={idx} className="text-4xl font-bold mt-12 mb-6 text-[var(--text-1)]">
                    {paragraph.replace(/^#\s*/, '')}
                  </h1>
                );
              }

              // Handle lists
              if (paragraph.match(/^[\-\*]\s/)) {
                return (
                  <ul key={idx} className="ml-6 mb-2">
                    <li className="text-[var(--text-2)]">
                      {paragraph.replace(/^[\-\*]\s/, '')}
                    </li>
                  </ul>
                );
              }

              // Handle bold
              let content = paragraph;
              content = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
              content = content.replace(/__(.*?)__/g, '<strong>$1</strong>');

              // Handle italic
              content = content.replace(/\*(.*?)\*/g, '<em>$1</em>');
              content = content.replace(/_(.*?)_/g, '<em>$1</em>');

              // Regular paragraph
              return (
                <p 
                  key={idx} 
                  className="mb-6 leading-relaxed text-[var(--text-2)] text-lg"
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              );
            })}
          </div>
        </div>

        {/* Footer - Related info */}
        <footer className="mt-12 pt-8 border-t border-[var(--surface-4)]">
          <div className="bg-[var(--surface-2)] rounded-xl p-6 border border-[var(--surface-4)]">
            <h3 className="text-lg font-semibold text-[var(--text-1)] mb-2">
              Want to compare laptops?
            </h3>
            <p className="text-[var(--text-2)] mb-4">
              Use our AI-powered comparison tool to find the perfect laptop for your needs.
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="px-6 py-3 bg-[var(--brand)] text-white rounded-lg hover:opacity-90 transition font-medium"
            >
              Try Comparison Tool
            </button>
          </div>
        </footer>
      </article>
    </div>
  );
};
